import React from "react";
import { AppBar, Toolbar, Box, Paper, Stack, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { SplitColumns } from 'components/SplitGrid'
import ApiService from "services/ApiService";
import { toDataGridRows, toDataGridRows4crsrd } from 'utils/commonFunc';
import ItsWebMap from "map/ItsWebMap";
import MapIcon from '@mui/icons-material/Map';
import { BoxContainer, LeftBottomStack, AlertInfoSnackbar } from "components/CustomMui";
import { Link2SelectViewDataGrid, Link2SelectCrsrdDataGrid } from "components/CustomDataGrid";
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IndexPlusBackdrop } from "components/CustomMui";
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ItsQuestionPaper from "components/ItsQuestionPaper";
import { getCookie } from "utils/commonFunc";
import AvgSpeedByLink from "./components/AvgSpeedByLink";
import TrafficVolumeByCrsrd from "./components/TrafficVolumeByCrsrd";
import dayjs from "dayjs";
import etcStyles from "../../src/pages/css/etc.module.css"

// 캐시저장소 (크기는 10이나 임의로 변경 가능)
class CacheStore {
    constructor(limit = 100) {
        this.limit = limit;
        this.cache = new Map();
    }
    // 우선순위큐 적용, 조회를 하면 캐시 제거대상 후순위로 밀려남
    get(key) {
        if (!this.cache.has(key)) return null;
        const value = this.cache.get(key);
        this.cache.delete(key);
        this.cache.set(key, value);

        // 캐시저장소 콘솔
        // console.groupCollapsed(`⚡ [HIT] 캐시 적중! (현재 ${this.cache.size}/${this.limit}개)`);
        // console.log("🎯 찾은 키:", key);
        // console.log("📦 현재 캐시 상태 (Map 순서대로):", this.cache);
        
        // // 맵을 배열로 변환해서 보기 편하게 출력 (Key만 모아서 순서 확인)
        // console.log("📊 최신순 정렬 (오른쪽이 최신):", [...this.cache.keys()]);
        // console.groupEnd();

        return value;
    }

    set(key, value) {
        if (this.cache.has(key)) {
            this.cache.delete(key);
        } 
        // 용량이 꽉 찼으면 가장 오래된 데이터 삭제, 우선순위큐
        else if (this.cache.size >= this.limit) {
            // 캐시저장소 콘솔
            // const oldestKey = this.cache.keys().next().value;
            // console.log(`🗑️ [EVICT] 용량 초과로 삭제됨: ${oldestKey}`); // 삭제 로그
            this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(key, value);
    }
}

export default function Link2SelectView() {
    const mapRef = React.useRef();
    const [dataGridStatus, setDataGridStatus] = React.useState({ loading: true, linkRows: [], crsrdRows: [] });
    const [selectFeatures, setSelectFeatures] = React.useState({ features: {}, count: 0, length: 0 });
    // const [modalOpen, setModalOpen] = React.useState(false);
    const [questionOpen, setQuestionOpen] = React.useState(getCookie("stopWatchingToday") !== 'true');
    const [snackbarMessage, setSnackbarMessage] = React.useState(null);

    const [viewType, setViewType] = React.useState('trafficVolume'); // 뷰타입 : 평균속도 or 교통량
    const [avgSpeedDataByLink, setAvgSpeedDataByLink] = React.useState([]);
    const [trafficVolumeDataByCrsrd, setTrafficVolumeDataByCrsrd] = React.useState([]);

    const yesterday = dayjs().subtract(1, 'day');
    const [avgSpeedConfig, setAvgSpeedConfig] = React.useState({
        viewType: 'hourly',
        startDate: yesterday.hour(0),
        endDate: yesterday.hour(23),
    });
    const [trafficConfig, setTrafficConfig] = React.useState({
        viewType: 'hourly',
        startDate: yesterday.hour(0),
        endDate: yesterday.hour(23),
    });
    const apiCache = React.useRef({
        avgSpeed: new CacheStore(600),
        traffic: new CacheStore(100)
    });

    // 뷰타입 변경 핸들러
    const viewTypeHandler = (event, newViewType) => {
        if (newViewType !== null) {
            setViewType(newViewType);
        }
    };

    // 평균속도 API 호출 함수
    const fetchLinkData = async (id, startDate, endDate, startTime, endTime) => {
        const currentParams = { id: String(id), startDate, endDate, startTime, endTime };
        const paramKey = JSON.stringify(currentParams, Object.keys(currentParams).sort());
        const cachedData = apiCache.current.avgSpeed.get(paramKey);
        if (cachedData) {
            setAvgSpeedDataByLink({ ...cachedData });
            return;
        }
        try {
            const requestBody = { id: String(id), startDate: startDate, endDate: endDate, startTime: startTime, endTime: endTime };
            const url = `newdashboard/ctznweb/link2Statistics`;
            const response = await ApiService.post(url, requestBody);
            setAvgSpeedDataByLink(response.data);
            apiCache.current.avgSpeed.set(paramKey, response.data);
        } catch (error) {
            // console.error("❌ 링크 API 요청 실패:", error);
        }
    };

    // 교통량 API 호출 함수
    const fetchTrafficData = async (crsrdId, crsrdNm, sdtm = null, edtm = null, type = 'hourly') => {
        const currentParams = { crsrdId, sdtm, edtm, type };
        const paramKey = JSON.stringify(currentParams, Object.keys(currentParams).sort());
        const cachedItem = apiCache.current.traffic.get(paramKey);
        if (cachedItem) {
             setTrafficVolumeDataByCrsrd({ ...cachedItem.data });
             if (cachedItem.data.acsr) {
                mapRef.current?.SetAccessRoadText(cachedItem.data.acsr, cachedItem.centerCoords, crsrdNm);
             }
             return;
        }
        setTrafficVolumeDataByCrsrd({ acsr: [], values: [] }); 
        mapRef.current?.SetAccessRoadText([]);
        try {
            const endpoint = type === 'daily' ? 'GetAcsrDay' : 'GetAcsr';
            let url = `newdashboard/ctznweb/${endpoint}?crsrdId=${crsrdId}`;
            if (sdtm && edtm) {
                url += `&sdtm=${sdtm}&edtm=${edtm}`;
            }
            const response = await ApiService.get(url);
            
            let centerCoords = null;
            if (response.data && response.data.acsr) {
                if (selectFeatures && selectFeatures.features) {
                    const featureObj = Object.values(selectFeatures.features)[0];
                    if (featureObj) {
                        const cLng = featureObj.get ? featureObj.get('lng') : featureObj.data?.lng;
                        const cLat = featureObj.get ? featureObj.get('lat') : featureObj.data?.lat;
                        if (cLng && cLat) {
                            centerCoords = { lng: cLng, lat: cLat };
                        }
                    }
                }
                mapRef.current?.SetAccessRoadText(response.data.acsr, centerCoords, crsrdNm);
            }
            setTrafficVolumeDataByCrsrd(response.data);
            apiCache.current.traffic.set(paramKey, {
                data: response.data,
                centerCoords: centerCoords
            });
        } catch (error) {
            // console.error("❌ 교차로 API 요청 실패:", error);
        }
    };

    // 우측 목록 클릭 콜백함수
    const onRowClickHandler = (params) => {
        let targetId = null;
        if (viewType === 'avgSpeed') {
            targetId = params.row.LINK2_ID;
        } else {
            targetId = params.row.crsrdId;
        }
        const isAlreadySelected = selectFeatures.features && selectFeatures.features[targetId];
        if (isAlreadySelected) {
            setSelectFeatures({ features: {}, count: 0, length: 0 });
            mapRef.current?.DeSelectAll();
            if (viewType === 'trafficVolume') {
                setTrafficVolumeDataByCrsrd([]);
            }
        } else {
            if (viewType === 'avgSpeed') {
                const center = params.row.GEO_CENTER;
                const linkId = params.row.LINK2_ID;
                if (center) {
                    mapRef.current?.SetViewCenter(center.x+0.01, center.y, linkId);
                    mapRef.current?.ShowTooltip(linkId, center.x, center.y);
                }
                setSelectFeatures({
                    count: 1,
                    length: 0,
                    features: {
                        [linkId]: {
                            data: params.row,
                            get: (key) => {
                                const mapping = { 'name': params.row.LINK2_NAME, 'fname': params.row.INTS_FNAME, 'tname': params.row.INTS_TNAME, 'id': linkId, 'lat': center.y, 'lng': center.x, 'len': params.row.LINK2_LEN };
                                return mapping[key];
                            }
                        }
                    }
                });
            } 
            else if (viewType === 'trafficVolume') {
                const { lng, lat, crsrdId, crsrdNm } = params.row;
                if (lng && lat) {
                    mapRef.current?.DeSelectAll();
                    mapRef.current?.SetViewCenter(lng+0.0025, lat, crsrdId, 17);
                    // mapRef.current?.ShowTooltip(crsrdId, lng, lat);
                    setTrafficVolumeDataByCrsrd([]);
                    setSelectFeatures({
                        count: 1,
                        length: 0,
                        features: {
                            [crsrdId]: {
                                data: params.row, 
                                get: (key) => {
                                    const mapping = {'name': crsrdNm, 'id': crsrdId, 'lat': lat, 'lng': lng };
                                    return mapping[key];
                                }
                            }
                        }
                    });
                }
            }
        }
    }

    const handleMapSelect = (result) => {
        setSelectFeatures(result);
        if (result && result.count > 0) {
            const feature = Object.values(result.features)[0];
            if (feature && typeof feature.get === 'function') {
                const crsrdId = feature.get('crsrdId');
                const lng = feature.get('lng');
                const lat = feature.get('lat');
                if (crsrdId && lng && lat) {
                    const numLng = parseFloat(lng);
                    const numLat = parseFloat(lat);
                    mapRef.current?.SetViewCenter(numLng+0.0025, numLat, crsrdId, 17);
                    // mapRef.current?.ShowTooltip(crsrdId, numLng, numLat);
                }
            }
        }
    };

    const currentSelectionModel = React.useMemo(() => {
        if (!selectFeatures || !selectFeatures.features) return [];
        const ids = Object.values(selectFeatures.features).map(item => {
            let extractedId = null;
            if (item.data) {
                if (viewType === 'avgSpeed') extractedId = item.data.LINK2_ID || item.data.id;
                else extractedId = item.data.crsrdId || item.data.id;
            } 
            else if (typeof item.get === 'function') {
                if (viewType === 'avgSpeed') {
                    const linkId = item.get('LINK2_ID');
                    const id = item.get('id');
                    extractedId = linkId || id;
                } else {
                    const crsrdId = item.get('crsrdId');
                    const id = item.get('id');
                    extractedId = crsrdId || id;
                }
            }
            return extractedId ? String(extractedId) : null;
        }).filter(id => id !== null);
        return ids;
    }, [selectFeatures, viewType]);

    const fetch = async (fromData) => {
        try {
            const [linkRes, crsrdRes] = await Promise.all([
                ApiService.get("itsWeb/link2Select"),
                ApiService.get("newdashboard/ctznweb/GetCrsrd")
            ]);

            if (linkRes.status === 200 && crsrdRes.status === 200) {
                const linkData = linkRes.data;
                const crsrdData = crsrdRes.data;
                // newdashboard/ctznweb/GetCrsrd에서 받은 API 데이터를 기존 itsweb/link2Select에서 받아오던 교차로 API 형식으로 파싱
                const convertedIntersection = {
                    type: "FeatureCollection",
                    features: crsrdData.map((item) => ({
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [item.lng, item.lat] 
                        },
                        properties: {
                            crsrdId: item.crsrdId,
                            crsrdNm: item.crsrdNm,
                            lng: item.lng,
                            lat: item.lat,
                            dt: item.dt,
                            trf: item.trf
                        }
                    }))
                };

                const linkRows = toDataGridRows(linkData.table);
                const crsrdRows = toDataGridRows4crsrd(crsrdData);
            
                setDataGridStatus({
                    loading: false,
                    linkRows: linkRows,
                    crsrdRows: crsrdRows
                })
                mapRef?.current?.AddLink2GeoJson(linkData.geoJson, convertedIntersection, handleMapSelect, setSnackbarMessage, true, viewType);
                mapRef?.current?.SetMapLayerVisibility(viewType);
                mapRef.current?.DefaultViewZoom();
            }
        } catch (error) {
            // console.error("초기 api 데이터 호출 실패:", error);
        }
    }

    React.useEffect(() => {
        fetch();
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (mapRef.current) {
            mapRef.current.DeSelectAll();
            setSelectFeatures({ features: {}, count: 0, length: 0 });
            if (mapRef.current.SetMapLayerVisibility) {
                mapRef.current.SetMapLayerVisibility(viewType);
            }
        }
    }, [viewType]);

    return (
        <Box className={`${etcStyles.container}`} sx={{ backgroundColor: '#f3f4f9', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
                <Toolbar variant="dense">
                    <Box 
                        component="img" 
                        src="/favicon.ico"
                        alt="logo" 
                        sx={{ height: 24, mr: 2 }} 
                    />
                    <Typography variant="h6" component="div">
                        수원시 ITS 평균속도 · 교통량 조회
                    </Typography>
                </Toolbar>
            </AppBar>
            <Paper sx={{ p: 2, flexGrow: 1, width: '100%', backgroundColor: '#f3f4f9' }}>
                <SplitColumns sizes={[0.2, 0.7]}>
                    <Box style={{ 
                        height: '100%', 
                        width: '100%', 
                        position: 'absolute',
                        backgroundColor: '#ffffff'
                    }}>
                        {viewType === 'avgSpeed' && (
                            <Link2SelectViewDataGrid
                                rows={dataGridStatus.linkRows}
                                loading={dataGridStatus.loading}
                                onRowClick={onRowClickHandler}
                                rowSelectionModel={currentSelectionModel}
                                lightMode={true}
                                getRowId={(row) => {
                                    if (row.isGroupHeader) {
                                        return String(row.id);
                                    }
                                    return row.LINK2_ID ? String(row.LINK2_ID) : String(row.id);
                                }}
                                sx={{
                                    '& .MuiDataGrid-main': { color: '#333' },
                                    '& .MuiDataGrid-columnHeaders': { 
                                        bgcolor: '#f5f5f5', 
                                        color: '#333' 
                                    },
                                    '& .MuiDataGrid-cell': { 
                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                        '&:focus': { outline: 'none' },
                                        '&:focus-within': { outline: 'none' }
                                    },
                                    '& .group-header-row': {
                                        bgcolor: 'rgb(83 100 255 / 20%)',
                                        color: '#919cff',
                                        '&:hover': { bgcolor: 'rgb(83 100 255 / 30%)' },
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: 'rgb(83 100 255 / 12%)',
                                    },
                                    '& .MuiDataGrid-row.Mui-selected': {
                                        backgroundColor: 'rgb(83 100 255 / 20%) !important',
                                        color: '#333 !important',
                                    },
                                    '& .MuiDataGrid-row.Mui-selected:hover': {
                                        backgroundColor: 'rgb(83 100 255 / 30%) !important',
                                    },
                                    '& .MuiDataGrid-virtualScroller': {
                                        '&::-webkit-scrollbar-thumb': { 
                                            bgcolor: 'rgba(0,0,0,0.2)', 
                                            borderRadius: '3px' 
                                        }
                                    },
                                    '& .MuiDataGrid-cell .MuiTypography-root': {
                                        color: '#333 !important'
                                    },
                                    '& .group-header-row .MuiTypography-root': {
                                        color: '#333 !important',
                                        fontWeight: 'bold !important'
                                    },
                                    '& .group-header-row .MuiSvgIcon-root': {
                                        color: '#333 !important'
                                    },
                                    '& .MuiDataGrid-row.Mui-selected .MuiTypography-root': {
                                        color: '#333 !important'
                                    }
                                }}
                            />
                        )}
                        {viewType === 'trafficVolume' && (
                            <Link2SelectCrsrdDataGrid
                                rows={dataGridStatus.crsrdRows}
                                loading={dataGridStatus.loading}
                                onRowClick={onRowClickHandler}
                                rowSelectionModel={currentSelectionModel}
                                lightMode={true}
                                getRowId={(row) => {
                                    return row.crsrdId ? String(row.crsrdId) : String(row.id);
                                }}
                                sx={{
                                    '& .MuiDataGrid-main': { 
                                        color: '#333', 
                                        bgcolor: 'transparent'
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        bgcolor: '#f5f5f5',
                                        color: '#333',
                                        borderBottom: '1px solid rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiDataGrid-cell': { 
                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                        '&:focus': { outline: 'none' },
                                        '&:focus-within': { outline: 'none' }
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        bgcolor: 'rgb(83 100 255 / 12%)',
                                    },
                                    '& .MuiDataGrid-row.Mui-selected': {
                                        backgroundColor: 'rgb(83 100 255 / 20%) !important',
                                        color: '#333 !important',
                                    },
                                    '& .MuiDataGrid-row.Mui-selected:hover': {
                                        backgroundColor: 'rgb(83 100 255 / 30%) !important',
                                    },
                                    '& .MuiDataGrid-virtualScroller': {
                                        '&::-webkit-scrollbar-thumb': { 
                                            bgcolor: 'rgba(0,0,0,0.2)', 
                                            borderRadius: '3px' 
                                        }
                                    },
                                    '& .MuiDataGrid-cell .MuiTypography-root': {
                                        color: '#333 !important'
                                    },
                                    '& .MuiDataGrid-row.Mui-selected .MuiTypography-root': {
                                        color: '#333 !important'
                                    },
                                }}
                            />
                        )}
                    </Box>
                    <BoxContainer>
                        <Paper variant="outlined" sx={{ 
                            height: '100%', 
                            width: '100%', 
                            p: 1, 
                            pl: 2,
                            backgroundColor: '#ffffff',
                            borderColor: '#e0e0e0'
                        }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                                <Typography className={`${etcStyles.pgTit}`} variant="h6" sx={{color: '#333333' }}>
                                    {viewType === 'avgSpeed' ? '통계 대상 도로 선택' : '교차로 접근로별 교통량'}
                                    {viewType === 'avgSpeed' && (
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        검색하면 도로구간명과 시점-종점에서의 검색이 필터링 되어 안내됩니다.
                                                    </Typography>
                                                    {/* 안내 사진 - 경로를 실제 프로젝트에 맞게 수정하세요 */}
                                                    <Box 
                                                        component="img" 
                                                        src="/images/help_search_guide.png" 
                                                        alt="검색 가이드"
                                                        sx={{ width: '100%', borderRadius: '4px', border: '1px solid #ddd' }}
                                                    />
                                                </Box>
                                            }
                                            placement="right"
                                            arrow
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#ffffff',
                                                        color: '#333',
                                                        boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
                                                        maxWidth: 350,
                                                        border: '1px solid #e0e0e0'
                                                    }
                                                },
                                                arrow: {
                                                    sx: { color: '#ffffff' }
                                                }
                                            }}
                                        >
                                            <HelpOutlineIcon sx={{ ml: 1, fontSize: '1.2rem', color: '#5364FF', cursor: 'pointer', mb: '-5px' }} />
                                        </Tooltip>
                                    )}
                                </Typography>
                                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={3}>
                                    <ToggleButtonGroup
                                        className={`${etcStyles.toggleBtn}`}
                                        value={viewType}
                                        exclusive
                                        onChange={viewTypeHandler}
                                        aria-label="view type"
                                        size="small"
                                        sx={{
                                            gap: 1,
                                            '& .MuiToggleButtonGroup-grouped': {
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                backgroundColor: 'transparent',
                                                color: '#333333',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#5364FF',
                                                    color: '#ffffff',
                                                    '&:hover': {
                                                        backgroundColor: '#5364FF',
                                                    }
                                                },
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                }
                                            }
                                        }}
                                    >
                                        <ToggleButton value="trafficVolume" sx={{ px: 3 }}>교통량</ToggleButton>
                                        <ToggleButton value="avgSpeed" sx={{ px: 3 }}>평균속도</ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                            </Stack>
                        </Paper>
                        <Paper variant="outlined" sx={{ 
                            height: '100%', 
                            width: '100%', 
                            display: 'grid', 
                            position:'relative',
                            backgroundColor: '#ffffff',
                            borderColor: '#e0e0e0'
                        }}>
                            <ItsWebMap
                                ref={mapRef}
                                lightMode={true}
                                initViewData={{ zoom: 15, center: [127.0480244, 37.2993926] }}>
                            </ItsWebMap>
                            {/* 평균속도 오버레이 */}
                            {viewType === 'avgSpeed' && selectFeatures.count > 0 && (
                                <Box className={etcStyles.popPnl}>
                                    {(() => {
                                        const featureObj = Object.values(selectFeatures.features)[0];
                                        if (!featureObj) return null;
                                        const isCrsrdData = (featureObj.data && featureObj.data.crsrdId) || (typeof featureObj.get === 'function' && featureObj.get('crsrdId'));
                                        if (isCrsrdData) return null;
                                        let linkId = null;
                                        if (typeof featureObj.get === 'function') {
                                            linkId = featureObj.get('LINK2_ID') || featureObj.get('id');
                                        } else {
                                            linkId = featureObj.LINK2_ID || featureObj.id;
                                        }
                                        if (!linkId) return null;
                                        const roadName = featureObj.get ? `${featureObj.get('name')} (${featureObj.get('fname')}-${featureObj.get('tname')})` : '도로명 없음';
                                        const featureData = {
                                            ...(featureObj.getProperties ? featureObj.getProperties() : featureObj),
                                            name: roadName,
                                            roadNm: roadName,
                                            LINK2_ID: linkId
                                        };

                                        return (
                                            <AvgSpeedByLink
                                                feature={featureData}
                                                onClose={() => mapRef.current?.DeSelectAll()}
                                                data={avgSpeedDataByLink}
                                                lightMode={true}
                                                onSearch={(sdtm, edtm, type) => {
                                                    let startDate, endDate, startTime, endTime;

                                                    if (type === 'hourly') {
                                                        startDate = sdtm.substring(0, 8);
                                                        startTime = sdtm.substring(8, 10);
                                                        endDate = edtm.substring(0, 8);
                                                        endTime = edtm.substring(8, 10);
                                                    } else  {
                                                        startDate = sdtm;
                                                        endDate = edtm;
                                                        startTime = "00";
                                                        endTime = "23"; 
                                                    }

                                                    fetchLinkData(linkId, startDate, endDate, startTime, endTime);
                                                }}
                                                initialConfig={avgSpeedConfig}
                                                onConfigChange={(newCfg) => setAvgSpeedConfig(prev => ({ ...prev, ...newCfg }))}
                                            />
                                        );
                                    })()}
                                </Box>
                            )}
                            {/* 교통량 오버레이 */}
                            {viewType === 'trafficVolume' && selectFeatures.count > 0 && (
                                <Box className={etcStyles.popPnl}>
                                {(() => {
                                    const featureObj = Object.values(selectFeatures.features)[0];
                                    const targetId = featureObj?.data?.crsrdId;
                                    const targetNm = featureObj?.data?.crsrdNm;
                                    if (!featureObj || !targetId) return null;
                                    return (
                                        <TrafficVolumeByCrsrd 
                                            feature={featureObj.data} 
                                            onClose={() => mapRef.current?.DeSelectAll()}
                                            data={trafficVolumeDataByCrsrd}
                                            lightMode={true}
                                            onSearch={(startDate, endDate, type) => {
                                                fetchTrafficData(targetId, targetNm, startDate, endDate, type);
                                            }}
                                            initialConfig={trafficConfig}
                                            onConfigChange={(newCfg) => setTrafficConfig(prev => ({ ...prev, ...newCfg }))}
                                        />
                                    );
                                })()}
                            </Box>
                            )}
                            <LeftBottomStack className={etcStyles.mapEtcBtn} spacing={1}>
                                <Tooltip placement="left" title="도움말">
                                    <Fab sx={{ 
                                        backgroundColor: '#5364FF !important',
                                        color: '#fff !important',
                                        '&:hover': { backgroundColor: '#919cff !important' }
                                    }} onClick={() => setQuestionOpen(true)}>
                                        <QuestionMarkIcon />
                                    </Fab>
                                </Tooltip>
                                <Tooltip placement="left" title="전체 영역 보기">
                                    <Fab sx={{ 
                                        backgroundColor: '#5364FF !important',
                                        color: '#fff !important',
                                        '&:hover': { backgroundColor: '#919cff !important' }
                                    }} onClick={mapRef.current?.DefaultViewZoom}>
                                        <MapIcon />
                                    </Fab>
                                </Tooltip>
                             </LeftBottomStack>
                        </Paper>
                    </BoxContainer>
                </SplitColumns>
            </Paper>
            <IndexPlusBackdrop open={questionOpen} onClick={() => setQuestionOpen(false)}>
                <Box>
                    <ItsQuestionPaper/>
                </Box>                
            </IndexPlusBackdrop>
            <AlertInfoSnackbar snackbarMessage={snackbarMessage} setSnackbarMessage={setSnackbarMessage} />
        </Box>
    )
}
